import { _lockUIandExecute } from '../../other/ui-lock.js';
import {
  activeV6FrameConfigVersion,
  getDealerMacroVersion,
  hasOptimizedBlobStorage,
  runBootLoaderEvents,
  setGlobalV6Version,
  v6ConfigEntryPoint,
  v6ConfigManager,
  v6FrameUserMode,
  V6MacroVersion,
  V6OfflineCause,
  v6SupportsVersion,
  versionToInteger
} from './v6-config.js';
import { ApiCommunications } from '../webmodule-interop.js';
import { decideBootVersion, shouldReloadOnReconnect } from '../../other/general/v6-boot-cache-decisions.js';
import { isEmptyOrSpace } from '../../other/ui/string-helper-functions.js';
import { litModalScreenFactoryImpl } from '../../other/ui/modal-factory-lit.js';
import { modalScreenFactoryImpl } from '../../other/ui/modal-factory.js';
import { noSqlDb } from '../../database/no-sql-db.js';
import { pageReload } from '../../other/ui/resource-resolver.js';
import { sharedExecute } from '../../other/common/helpers/callbacks.js';
import { showError } from '../../other/ui/show-error.js';
import { userSecurity } from '../../other/api/user-security.js';

// eslint-disable-next-line no-var -- var declared as it is replaced by
declare var __localdebug__: string;
export const isLocalV6Debug = __localdebug__ === 'true';

let _stale = false;
// Set once the loader falls back to a cached version because the supplier is offline; stays true
// (and keeps _stale true) until the live version is reconfirmed on reconnect (@SUPOFF-RETURN).
let _bootedFromCache = false;
// The last supplier id we heard about, live or offline - used to find the right cache entry on a
// call where getDealerMacroVersion couldn't reach the server at all (no supplierId to key on).
let _lastKnownSupplierId: string | undefined;

// Same horizon as the neighbouring V6 caches (application-initialization.ts's expiresIn.oneMonth).
// The noSqlDb default (7 days) would strand a dealer who doesn't visit for a week with nothing to
// boot from, on a browser that otherwise has a perfectly good last-known-good version cached.
const oneMonthSeconds = 60 * 60 * 24 * 30;

export function v6bootloaderStale() {
  _stale = true;
}

export function v6MacroVersionCacheKey(supplierId: string): string {
  return `v6:macroversion:${supplierId}`;
}

// Test seam: window.location.reload() can't run in a unit test. Production always uses the real
// pageReload; a test overrides it to observe whether a reload would have happened.
let _pageReloadEvent: () => void = pageReload;
export function setV6PageReloadEvent(event: () => void): void {
  _pageReloadEvent = event;
}

/**
 * Reason codes for V6 script loader failures
 */
export enum V6LoaderFailureReason {
  /** No failure - load was successful */
  None = 'none',
  /** Supplier is offline or unreachable */
  SupplierOffline = 'supplier-offline',
  /** Version string is empty or invalid */
  InvalidVersion = 'invalid-version',
  /** Script failed to load from CDN */
  ScriptLoadFailed = 'script-load-failed',
  /** V6 config did not initialize correctly after load */
  ConfigInitFailed = 'config-init-failed',
  /** Loaded version does not support optimized blob storage */
  UnsupportedVersion = 'unsupported-version'
}

/**
 * Result of the V6 frame config script loader
 */
export interface V6LoaderResult {
  /** Whether the load was successful */
  success: boolean;
  /** The version that was loaded (or attempted) */
  version?: string;
  /** Reason for failure if success is false */
  failureReason?: V6LoaderFailureReason;
  /** Offline cause from the supplier (if applicable) */
  offlineCause?: V6OfflineCause;
  /** Message from the supplier or error details */
  message?: string;
  /** The underlying error if one occurred */
  error?: Error;
  /** True when this load used a cached version because the supplier was offline (@SUPOFF-BOOT-002) */
  bootedFromCache?: boolean;
  /** The supplier this result concerns - lets a caller target the right GlobalSupplierNotification */
  supplierId?: string;
  /**
   * True only when `bootedFromCache` is set AND the supplier's own state actually changed (the
   * server told us it's offline) - as opposed to our own version check simply failing to reach the
   * server at all (typically WebModule itself restarting), which must not trigger the one-off
   * offline notice since the supplier's state hasn't changed (round 3 review).
   */
  showOfflineNotice?: boolean;
}

let scriptElement: HTMLScriptElement | null = null;
let _loadedVersion: string | undefined = undefined;

/**
 * Undoes a script load completely - the script tag, the config manager it registered, and the
 * version bookkeeping - so nothing about it can be mistaken for "already loaded" afterwards.
 *
 * Every failure path after the script tag actually loads (UnsupportedVersion, ConfigInitFailed,
 * ScriptLoadFailed) MUST call this. Before this fix, UnsupportedVersion left `globalThis.v6ConfigManager`
 * live and ConfigInitFailed's catch removed only the script tag - either way `_loadedVersion` or
 * `v6ConfigManager()?.ver(...)` still answered "yes" on the next call, so a Try Again after a
 * rejected load passed the "already loaded" fast path and the app started on the very version the
 * loader had just refused.
 */
function disposeLoadedV6Script(): void {
  scriptElement?.remove();
  globalThis.v6ConfigManager?.dispose?.();
  globalThis.v6ConfigManager = undefined;
  scriptElement = null;
  _loadedVersion = undefined;
}

/**
 * Builds a successful V6LoaderResult, attaching the supplier id and, only when this was actually a
 * cache/server-last-good boot, the offline detail the caller needs to show its one-off notice.
 * `showOfflineNotice` is deliberately narrower than `bootedFromCache`: it's false when the "offline"
 * signal was really just our own version check failing to reach the server at all
 * (`confirmedOffline: false` - see decideBootVersion) - the supplier's state hasn't changed then, so
 * no notice should fire, even though the boot itself still proceeds on the cached version.
 */
function buildSuccessResult(
  version: string,
  bootedFromCache: boolean,
  offlineDetail: { offlineCause: V6OfflineCause; message?: string; confirmedOffline: boolean } | undefined
): V6LoaderResult {
  return {
    success: true,
    version,
    bootedFromCache,
    supplierId: _lastKnownSupplierId,
    offlineCause: offlineDetail?.offlineCause,
    message: offlineDetail?.message,
    showOfflineNotice: bootedFromCache ? offlineDetail?.confirmedOffline : undefined
  };
}

/**
 * Test-only: this loader is a long-lived module singleton by design (the whole point is that it
 * remembers what's loaded across calls). A test file exercising it across several `it()`s needs to
 * clear that state between cases; production code has no reason to call this.
 */
export function __resetV6FrameConfigLoaderStateForTests(): void {
  _stale = false;
  _bootedFromCache = false;
  _lastKnownSupplierId = undefined;
  scriptElement = null;
  _loadedVersion = undefined;
  _pageReloadEvent = pageReload;
}

/**
 * Wrapped loader that prevents concurrent executions.
 * Use this as the public API.
 */
export const v6FrameConfigScriptLoader = sharedExecute(v6FrameConfigScriptLoaderInternal);

/**
 * Internal implementation of the V6 frame config script loader.
 * Loads the V6 config script dynamically, with version checking and cleanup.
 * Returns a compound result with success/failure and extended information.
 */
async function v6FrameConfigScriptLoaderInternal(comms: ApiCommunications): Promise<V6LoaderResult> {
  const isLocalV6 = isLocalV6Debug;

  // Already loaded and confirmed - skip the live check entirely. A cache-boot keeps _stale set so
  // this fast path is skipped until the live version is reconfirmed (@SUPOFF-RETURN-001/002).
  if (!_stale && _loadedVersion && v6ConfigManager()?.ver(_loadedVersion)) {
    return { success: true, version: _loadedVersion, supplierId: _lastKnownSupplierId };
  }

  // Only fetch dealer version when we need it (stale check or first load)
  const dealerVersion = await getDealerMacroVersion(comms);

  if (dealerVersion?.supplierId) {
    _lastKnownSupplierId = dealerVersion.supplierId;
    // Cache every valid live result - the last-known-good boot point if the supplier later drops
    // before we get a fresh answer (@SUPOFF-BOOT-001).
    if (!isEmptyOrSpace(dealerVersion.configVersion)) {
      await noSqlDb.setItem(v6MacroVersionCacheKey(dealerVersion.supplierId), dealerVersion, oneMonthSeconds);
    }
  }

  let requiredVersion: string;
  let offlineDetail: { offlineCause: V6OfflineCause; message?: string; confirmedOffline: boolean } | undefined;

  if (isLocalV6) {
    requiredVersion = activeV6FrameConfigVersion() ?? '';
  } else {
    const cacheKey = _lastKnownSupplierId ? v6MacroVersionCacheKey(_lastKnownSupplierId) : undefined;
    const cached = cacheKey ? await noSqlDb.getItem<V6MacroVersion>(cacheKey) : null;

    const outcome = decideBootVersion(dealerVersion, cached ?? undefined);

    if (outcome.kind === 'blocked') {
      return {
        success: false,
        failureReason: outcome.genuinelyOffline
          ? V6LoaderFailureReason.SupplierOffline
          : V6LoaderFailureReason.InvalidVersion,
        offlineCause: outcome.offlineCause,
        message: outcome.message,
        supplierId: _lastKnownSupplierId
      };
    }

    requiredVersion = outcome.requiredVersion;
    if (outcome.kind === 'cache') {
      offlineDetail = {
        offlineCause: outcome.offlineCause,
        message: outcome.message,
        confirmedOffline: outcome.confirmedOffline
      };
    }
  }

  // Coming back online after a cache-boot: reconcile the version actually running against the
  // now-confirmed live version. A mismatch means an in-place script swap isn't safe (@SUPOFF-RETURN-002).
  if (_bootedFromCache && !offlineDetail && !isLocalV6) {
    if (shouldReloadOnReconnect(_loadedVersion, requiredVersion)) {
      _pageReloadEvent();
      return { success: true, version: _loadedVersion, bootedFromCache: false, supplierId: _lastKnownSupplierId };
    }
    _bootedFromCache = false;
  }

  _bootedFromCache = !!offlineDetail;
  // Keep re-checking the live version on every subsequent call while running on a cached one.
  _stale = _bootedFromCache;

  // Already have this version loaded - exit
  if (v6ConfigManager()?.ver(requiredVersion)) {
    _loadedVersion = requiredVersion;
    return buildSuccessResult(requiredVersion, _bootedFromCache, offlineDetail);
  }

  // Different version already loaded - cleanup and reload
  if (scriptElement && _loadedVersion && _loadedVersion !== requiredVersion) {
    disposeLoadedV6Script();
  }

  // Need to load the script
  const v6Script = document.createElement('script');
  scriptElement = v6Script;

  setGlobalV6Version(requiredVersion === '' ? 0 : versionToInteger(requiredVersion), requiredVersion);

  const v6Source = isLocalV6
    ? `./v6/v6config-entrypoint.js`
    : `https://webmodule-softtech.azureedge.net/v6config/${requiredVersion}/v6config-entrypoint.js`;

  try {
    await new Promise<void>((resolve, reject) => {
      v6Script.onload = () => {
        // A synchronous throw anywhere in here (e.g. v6ConfigEntryPoint's validateConfigVerison
        // throwing a DevelopmentError) must still reject this promise. Uncaught, it would leave
        // the promise permanently pending: sharedExecute's in-flight call never resolves, so every
        // future v6FrameConfigScriptLoader call - including a dealer's Try Again - hangs forever
        // rather than getting a fresh attempt.
        try {
          _stale = _bootedFromCache;
          _loadedVersion = requiredVersion;
          const versionToUse = isLocalV6 ? (activeV6FrameConfigVersion() ?? 'v0.0.0') : requiredVersion;
          setGlobalV6Version(versionToInteger(versionToUse), versionToUse);
          const v6Events = v6ConfigEntryPoint(versionToUse)?.events;
          if (!v6Events) {
            reject(new Error(`V6 Config ${requiredVersion} did not load correctly`));
            return;
          }

          v6Events.setApiInjector(() => comms);
          v6Events.setErrorDialogEventHandler(showError);
          if (!v6SupportsVersion('v2.0.0')) {
            v6Events.setModalScreenFactory(modalScreenFactoryImpl, litModalScreenFactoryImpl);
            v6Events.setUILockAndExecuteEvent(_lockUIandExecute);
          }

          v6Events.setUserMode(userSecurity().isPowerUser() ? v6FrameUserMode.powerUser : v6FrameUserMode.standardUser);
          resolve();
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      };

      v6Script.onerror = event => {
        console.error(`Failed to load V6 script from ${v6Source}:`, event);
        if (event instanceof ErrorEvent) {
          console.error('Error message:', event.message);
          console.error('Error filename:', event.filename);
          console.error('Error lineno:', event.lineno);
          console.error('Error colno:', event.colno);
          console.error('Error stack:', event.error?.stack);
        }

        fetch(v6Source)
          .then(response => {
            if (!response.ok) {
              console.error(`Network response was not ok: ${response.status} ${response.statusText}`);
            } else {
              console.log('Script can be fetched directly, but failed to load via script tag');
            }
          })
          .catch(err => {
            console.error('Fetch attempt also failed:', err);
          });

        reject(new Error(`Failed to load V6 script from ${v6Source}`));
      };

      v6Script.setAttribute('src', v6Source);
      document.body.appendChild(v6Script);
    });

    await runBootLoaderEvents();

    if (!hasOptimizedBlobStorage()) {
      // The script loaded and initialized fully - v6ConfigManager is genuinely live for this
      // version - but we're rejecting it anyway, so it must not be left looking "already loaded".
      disposeLoadedV6Script();
      return {
        success: false,
        version: requiredVersion,
        failureReason: V6LoaderFailureReason.UnsupportedVersion,
        message: 'V6 version does not support optimized blob storage',
        supplierId: _lastKnownSupplierId
      };
    }

    return buildSuccessResult(requiredVersion, _bootedFromCache, offlineDetail);
  } catch (error) {
    console.error('V6 script load failed:', error);
    // Clean up on failure - includes disposing globalThis.v6ConfigManager, not just the script tag:
    // ConfigInitFailed reaches here with the script already registered against globalThis even
    // though `events` never came back, and it must not be left answering `ver()` truthfully.
    disposeLoadedV6Script();

    const err = error instanceof Error ? error : new Error(String(error));
    const isConfigInit = err.message.includes('did not load correctly');

    return {
      success: false,
      version: requiredVersion,
      failureReason: isConfigInit ? V6LoaderFailureReason.ConfigInitFailed : V6LoaderFailureReason.ScriptLoadFailed,
      message: err.message,
      error: err,
      supplierId: _lastKnownSupplierId
    };
  }
}
